// Selector buscable de una lista corta.
//
// Sustituye al `input` + `datalist` de Documentos: aquél era buscable pero el
// navegador decide cómo se pinta, así que no había forma de enseñar el estado y
// las unidades de cada carga dentro de la opción, que es justo lo que hace útil
// elegir sin salir a mirarlo.
//
// Es deliberadamente pequeño: sin dependencias, sin portal —vive en un bloque
// que no recorta— y con el teclado que se espera (flechas, Enter, Esc, Tab).
import { useEffect, useId, useRef, useState } from 'react'
import { apagadoCls, bordeTenueCls, tenueCls } from './estilos'

export interface OpcionCombo {
  valor: string
  /** Lo que identifica la opción. Se pinta en monoespaciada. */
  titulo: string
  /** Datos de apoyo, ya formateados. Se pintan apagados detrás del título. */
  detalle?: string
}

interface Props {
  dark: boolean
  opciones: OpcionCombo[]
  valor: string
  onChange: (valor: string) => void
  etiqueta: string
  placeholder?: string
}

export function Combobox({ dark, opciones, valor, onChange, etiqueta, placeholder }: Props) {
  const id = useId()
  const [abierto, setAbierto] = useState(false)
  const [texto, setTexto] = useState('')
  const [resaltada, setResaltada] = useState(0)
  const caja = useRef<HTMLDivElement>(null)

  const elegida = opciones.find(o => o.valor === valor)
  const q = texto.trim().toLowerCase()
  const filtradas = q
    ? opciones.filter(o =>
        o.titulo.toLowerCase().includes(q) || (o.detalle ?? '').toLowerCase().includes(q))
    : opciones

  // Cerrar al pulsar fuera. Sin esto el desplegable se queda abierto al ir a
  // cualquier otra parte de la pestaña.
  useEffect(() => {
    if (!abierto) return
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false)
    }
    document.addEventListener('mousedown', fuera)
    return () => document.removeEventListener('mousedown', fuera)
  }, [abierto])

  function elegir(o: OpcionCombo) {
    onChange(o.valor)
    setTexto('')
    setAbierto(false)
  }

  function teclas(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { setAbierto(false); return }
    if (e.key === 'Tab') { setAbierto(false); return }
    if (!abierto && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setAbierto(true); setResaltada(0); e.preventDefault(); return
    }
    if (!abierto) return
    if (e.key === 'ArrowDown') {
      setResaltada(i => Math.min(i + 1, filtradas.length - 1)); e.preventDefault()
    } else if (e.key === 'ArrowUp') {
      setResaltada(i => Math.max(i - 1, 0)); e.preventDefault()
    } else if (e.key === 'Enter') {
      const o = filtradas[resaltada]
      if (o) elegir(o)
      e.preventDefault()
    }
  }

  const marco = dark
    ? 'border-white/10 bg-white/[0.04] text-slate-100 focus-within:border-sky-500/60'
    : 'border-slate-200 bg-white text-slate-800 focus-within:border-sky-400'

  return (
    <div ref={caja} className="relative flex flex-col gap-1.5 max-w-[360px]">
      <label htmlFor={id} className={`text-[10px] font-bold uppercase tracking-wider ${tenueCls(dark)}`}>
        {etiqueta}
      </label>

      <div className={`flex items-center gap-2 h-[38px] px-3 rounded-lg border transition-colors duration-150 ${marco}`}>
        <input
          id={id}
          role="combobox"
          aria-expanded={abierto}
          aria-controls={`${id}-lista`}
          autoComplete="off"
          value={abierto ? texto : (elegida?.titulo ?? '')}
          placeholder={placeholder}
          onFocus={() => { setAbierto(true); setTexto(''); setResaltada(0) }}
          onChange={e => { setTexto(e.target.value); setResaltada(0); setAbierto(true) }}
          onKeyDown={teclas}
          className="flex-1 min-w-0 bg-transparent outline-none font-mono text-[13px] font-semibold"
        />
        <svg
          className={`w-3.5 h-3.5 shrink-0 transition-transform duration-150 ${abierto ? 'rotate-180' : ''} ${apagadoCls(dark)}`}
          viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M4 6.5l4 4 4-4" />
        </svg>
      </div>

      {abierto && (
        <ul
          id={`${id}-lista`}
          role="listbox"
          className={`absolute top-full left-0 right-0 z-30 mt-1 max-h-[280px] overflow-y-auto scroll-thin rounded-lg border shadow-sm ${
            dark ? 'border-white/10 bg-[#111827]' : 'border-slate-200 bg-white'
          }`}
        >
          {filtradas.map((o, i) => (
            <li key={o.valor} role="option" aria-selected={o.valor === valor}>
              <button
                type="button"
                onMouseEnter={() => setResaltada(i)}
                onClick={() => elegir(o)}
                className={`w-full flex items-baseline gap-2 px-3 py-2 text-left cursor-pointer transition-colors duration-150 ${
                  i > 0 ? `border-t ${bordeTenueCls(dark)}` : ''
                } ${
                  i === resaltada
                    ? dark ? 'bg-sky-500/12' : 'bg-sky-50'
                    : ''
                }`}
              >
                <span className={`font-mono text-[12px] font-semibold ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
                  {o.titulo}
                </span>
                {o.detalle && (
                  <span className={`text-[11px] tabular-nums truncate ${apagadoCls(dark)}`}>{o.detalle}</span>
                )}
                {o.valor === valor && (
                  <svg className="ml-auto w-3.5 h-3.5 shrink-0 text-sky-500" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 8.5l3.5 3.5L13 5" />
                  </svg>
                )}
              </button>
            </li>
          ))}
          {filtradas.length === 0 && (
            <li className={`px-3 py-3 text-[12px] ${apagadoCls(dark)}`}>—</li>
          )}
        </ul>
      )}
    </div>
  )
}
